// React Libraries
import React, { useState, useEffect } from 'react';

// React Query Library
import { useQuery } from '@tanstack/react-query';
import { getProductions } from 'services/api/production';

// MUI Components
import { Card, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

// Project Components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DataTableHeadCell from 'examples/Tables/DataTable/DataTableHeadCell';
import DataTableBodyCell from 'examples/Tables/DataTable/DataTableBodyCell';

// Search Context
import { useSearch } from 'layouts/production/Pages/SearchProvider';

// Components
import ProductionName from '../ProductionsData/components/productionName';
import AaroCustomerName from '../ProductionsData/components/aaroCustomerName';
import ProductionStatus from '../ProductionsData/components/status';
import { ProductionResponsibles } from '../ProductionsData/components/responsibles';
import { ProductionCreationDate } from '../ProductionsData/components/productionCreationDate';
import PinProduction from '../ProductionsData/components/pinProduction';
import FastEdit from '../ProductionsData/components/fastEdit';

// Config
import { config } from 'Constants';

const fetchPinnedProductions = async (userId) => {
    try {
        // Fetch productions where the user has pinned
        const response = await getProductions(`pinnedBy=${userId}`).then((rp) => rp.data);
        return response || { result: [] };
    } catch (error) {
        console.error('Error fetching pinned productions:', error);
        return { result: [] };
    }
};

// Main Component
export default function PinnedProductions() {
    const user = JSON.parse(localStorage.getItem('user'));

    const {
        data,
        isLoading: isLoadingPinnedProductions,
        error: isErrorPinnedProductions,
    } = useQuery({
        queryKey: ['fetchPinnedProductions', user?.userID],
        queryFn: () => fetchPinnedProductions(user?.userID),
        staleTime: 600000,
    });

    if (isLoadingPinnedProductions) return <div>Loading...</div>;
    if (isErrorPinnedProductions) return <div>Error: {isErrorPinnedProductions.message}</div>;

    // Ensure data.result is an array
    const pinnedProductions = Array.isArray(data?.result) ? data.result : [];

    // If no pinned productions, don't render anything
    if (pinnedProductions.length === 0) return null;

    return (
        <Card>
            <MDBox p={3} pb={0}>
                <MDTypography variant="h5" fontWeight="medium">
                    Sabitlediğim Üretimler
                </MDTypography>
            </MDBox>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <DataTableHeadCell width="15%" align={'left'}>
                                Üretim Adı
                            </DataTableHeadCell>
                            <DataTableHeadCell width="20%" align={'left'}>
                                Durum
                            </DataTableHeadCell>
                            <DataTableHeadCell width="15%" align={'left'}>
                                Oluşturulma Tarihi
                            </DataTableHeadCell>
                            <DataTableHeadCell width="15%" align={'left'}>
                                Müşteri
                            </DataTableHeadCell>
                            <DataTableHeadCell width="20%" align={'left'}>
                                Sorumlular
                            </DataTableHeadCell>
                            <DataTableHeadCell width="10%" align={'left'}>
                                İşlemler
                            </DataTableHeadCell>
                            <DataTableHeadCell width="5%" align={'left'}>
                                Düzenle
                            </DataTableHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pinnedProductions.map((row, index) => (
                            <TableRow key={row._id} className={row.isActive ? '' : 'inactiveRow'}>
                                <DataTableBodyCell align={'left'}>
                                    <ProductionName id={row._id} formattedValue={row.productionName} />
                                </DataTableBodyCell>
                                <DataTableBodyCell align={'left'}>
                                    <ProductionStatus status={row?.status} />
                                </DataTableBodyCell>
                                <DataTableBodyCell align={'left'}>
                                    <ProductionCreationDate productionID={row._id} date={row.createdAt} />
                                </DataTableBodyCell>
                                <DataTableBodyCell align={'left'}>
                                    <AaroCustomerName id={row._id} formattedValue={row.aaroCustomerName} />
                                </DataTableBodyCell>
                                <DataTableBodyCell align={'left'}>
                                    <ProductionResponsibles responsibles={row.responsibles} />
                                </DataTableBodyCell>
                                <DataTableBodyCell align={'left'} width={10}>
                                    <PinProduction
                                        isPinned={row?.pinnedBy?.includes(user?.userID)}
                                        productionID={row._id}
                                        productions={pinnedProductions}
                                        userID={user?.userID}
                                    />
                                </DataTableBodyCell>
                                <DataTableBodyCell align={'left'} width={10}>
                                    <FastEdit productionID={row._id} />
                                </DataTableBodyCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
}
